import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { resetPassword } from '../../services/auth';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  EnvelopeIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { PublicLayout } from '../../components/layout';

interface ForgotPasswordForm {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    setLoading(true);
    try {
      const result = await resetPassword(data.email);

      if (result.success) {
        setEmailSent(true);
        toast.success(
          'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني'
        );
      } else {
        toast.error(result.error || 'فشل في إرسال البريد الإلكتروني');
      }
    } catch (error) {
      toast.error('حدث خطأ غير متوقع');
      console.error('Reset password error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div
        className='min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'
      >
        <div className='max-w-md w-full text-center'>
          <div className='mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-green-100'>
            <CheckCircleIcon className='h-8 w-8 text-green-600' />
          </div>

          <h2 className='mt-6 text-3xl font-extrabold text-gray-900'>
            تم إرسال البريد الإلكتروني
          </h2>

          <p className='mt-4 text-gray-600'>
            تم إرسال رابط إعادة تعيين كلمة المرور إلى:
          </p>
          <p className='text-primary-600 font-medium'>{getValues('email')}</p>

          <p className='mt-4 text-sm text-gray-500'>
            تحقق من بريدك الإلكتروني واتبع التعليمات لإعادة تعيين كلمة المرور.
            إذا لم تجد البريد، تحقق من مجلد الرسائل المؤقتة (Spam).
          </p>

          <div className='mt-8 space-y-4'>
            <button
              onClick={() => setEmailSent(false)}
              className='w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
            >
              إرسال مرة أخرى
            </button>

            <Link
              to='/login'
              className='w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
            >
              العودة إلى تسجيل الدخول
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PublicLayout>
      <div className='bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          {/* Header */}
          <div className='text-center'>
            <div className='mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100'>
              <EnvelopeIcon className='h-6 w-6 text-primary-600' />
            </div>
            <h2 className='mt-6 text-3xl font-extrabold text-gray-900'>
              نسيت كلمة المرور؟
            </h2>
            <p className='mt-2 text-sm text-gray-600'>
              أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة تعيين كلمة المرور
            </p>
          </div>

          {/* Form */}
          <form className='mt-8 space-y-6' onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label
                htmlFor='email'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                البريد الإلكتروني
              </label>
              <div className='relative'>
                <input
                  {...register('email', {
                    required: 'البريد الإلكتروني مطلوب',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'البريد الإلكتروني غير صحيح',
                    },
                  })}
                  type='email'
                  className='block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500'
                  placeholder='أدخل بريدك الإلكتروني'
                />
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <EnvelopeIcon className='h-5 w-5 text-gray-400' />
                </div>
              </div>
              {errors.email && (
                <p className='mt-1 text-sm text-red-600'>
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Info Box */}
            <div className='bg-blue-50 border border-blue-200 rounded-md p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <svg
                    className='h-5 w-5 text-blue-400'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='mr-3'>
                  <h3 className='text-sm font-medium text-blue-800'>
                    ملاحظة مهمة
                  </h3>
                  <p className='mt-1 text-sm text-blue-700'>
                    ستتلقى بريد إلكتروني يحتوي على رابط لإعادة تعيين كلمة
                    المرور. الرابط صالح لمدة ساعة واحدة فقط.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='flex flex-col space-y-3'>
              <button
                type='submit'
                disabled={loading}
                className='w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {loading ? (
                  <LoadingSpinner size='small' />
                ) : (
                  'إرسال رابط إعادة التعيين'
                )}
              </button>

              <Link
                to='/login'
                className='w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
              >
                <ArrowRightIcon className='h-4 w-4 ml-2' />
                العودة إلى تسجيل الدخول
              </Link>
            </div>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ForgotPassword;
